// Switcharoonie SWAP animation designs (dev gallery: /sw). In the real game worms do NOT
// move at a switch - OWNERSHIP swaps, so each head's colour flips to its new owner at GO.
// These designs sell that moment: something visibly travels between the two worms (souls,
// beams, portals, sparks...) carrying the colours across. Drawn in PIXELS around the two
// (frozen) head positions; the caller draws the heads themselves and flips their colours
// at p = 0.5 (except where a design hides them - see HIDE_HEADS).
//
// drawSwitchSwapAnim(ctx, a, b, headR, p, t, variant)
//   a, b   : the two ends { x, y, colorFrom, colorTo } (colorTo = the new owner's colour)
//   headR  : head radius in pixels (effect sizes scale off this)
//   p      : swap progress 0..1 (the freeze HOLD window)
//   t      : absolute seconds, for shimmer/rotation
//   variant: 1..N (see SWITCH_ANIMS)

export interface SwapEnd { x: number; y: number; colorFrom: string; colorTo: string }

export const SWITCH_ANIMS: { n: number; label: string }[] = [
  { n: 1, label: "Soul orbs (crossing arcs)" },
  { n: 2, label: "Ghost trails" },
  { n: 3, label: "Lightning link" },
  { n: 4, label: "Portals" },
  { n: 5, label: "Twin beams" },
  { n: 6, label: "Vortex exchange" },
  { n: 7, label: "Orbit half-turn" },
  { n: 8, label: "Smoke swap" },
  { n: 9, label: "Spark stream" },
  { n: 10, label: "Glitch teleport" },
  { n: 11, label: "Yin-yang orbit" },
  { n: 12, label: "Comet crossover" },
  { n: 13, label: "DNA helix" },
  { n: 14, label: "Firework handoff" },
  { n: 15, label: "Pulse rings relay" },
  { n: 16, label: "Shadow dash" },
  { n: 17, label: "Star spin" },
  { n: 18, label: "Colour drip swap" },
  // ===== portal family (riffs on #4) =====
  { n: 19, label: "Portal: vortex spiral" },
  { n: 20, label: "Portal: iris gate" },
  { n: 21, label: "Portal: event horizon" },
  { n: 22, label: "Portal: gyro rings" },
  { n: 23, label: "Portal: ripple pool" },
  { n: 24, label: "Portal: rune circle" },
  { n: 25, label: "Portal: wormhole tunnel" },
  { n: 26, label: "Portal: shard gate" },
  { n: 27, label: "Portal: storm gate" },
  { n: 28, label: "Portal: ink well" },
  { n: 29, label: "Portal: event horizon + soul orbs" },
  { n: 30, label: "Portal: event horizon + fireworks" },
  { n: 31, label: "Portal: event horizon + fireworks (over)" },
];

// Designs that want the demo/host to NOT draw the heads mid-swap (they vanish + reappear).
export const HIDE_HEADS: Record<number, [number, number]> = {
  4: [0.25, 0.75], // portals: inside the wormhole
  8: [0.2, 0.8],   // smoke swap: hidden in the puffs
  10: [0.15, 0.85], // glitch: dissolved
  19: [0.25, 0.75], 20: [0.25, 0.75], 21: [0.25, 0.75], 22: [0.25, 0.75], 23: [0.25, 0.75],
  24: [0.25, 0.75], 25: [0.25, 0.75], 26: [0.25, 0.75], 27: [0.25, 0.75], 28: [0.25, 0.75],
  29: [0.25, 0.75], 30: [0.25, 0.75], 31: [0.25, 0.75],
};

const TAU = Math.PI * 2;
const clamp01 = (v: number) => Math.max(0, Math.min(1, v));
const easeInOut = (v: number) => (v < 0.5 ? 2 * v * v : 1 - Math.pow(-2 * v + 2, 2) / 2);
const easeOut = (v: number) => 1 - Math.pow(1 - v, 3);
const rnd = (i: number) => { const s = Math.sin(i * 127.1 + 311.7) * 43758.5453; return s - Math.floor(s); };
function rgba(hex: string, a: number): string {
  const h = hex.replace("#", "");
  const n = parseInt(h.length === 3 ? h.split("").map((c) => c + c).join("") : h, 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${clamp01(a)})`;
}
// Quadratic bezier point between the ends, bent sideways by `bend` px.
function bez(ax: number, ay: number, bx: number, by: number, bend: number, u: number): [number, number] {
  const mx = (ax + bx) / 2, my = (ay + by) / 2;
  const d = Math.hypot(bx - ax, by - ay) || 1;
  const nx = -(by - ay) / d, ny = (bx - ax) / d;
  const cx = mx + nx * bend, cy = my + ny * bend;
  const q = 1 - u;
  return [q * q * ax + 2 * q * u * cx + u * u * bx, q * q * ay + 2 * q * u * cy + u * u * by];
}
function dot(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, fill: string) {
  ctx.fillStyle = fill; ctx.beginPath(); ctx.arc(x, y, Math.max(0.5, r), 0, TAU); ctx.fill();
}
function ring(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, w: number, stroke: string) {
  ctx.strokeStyle = stroke; ctx.lineWidth = Math.max(0.6, w); ctx.beginPath(); ctx.arc(x, y, Math.max(0.5, r), 0, TAU); ctx.stroke();
}

// One travelling "soul": a glowing orb with a short fading tail along a bent path.
function soul(ctx: CanvasRenderingContext2D, a: { x: number; y: number }, b: { x: number; y: number }, bend: number, u: number, r: number, color: string) {
  for (let k = 4; k >= 1; k--) {
    const [tx, ty] = bez(a.x, a.y, b.x, b.y, bend, Math.max(0, u - k * 0.045));
    dot(ctx, tx, ty, r * (0.5 - k * 0.08), rgba(color, 0.5 - k * 0.1));
  }
  const [hx, hy] = bez(a.x, a.y, b.x, b.y, bend, u);
  dot(ctx, hx, hy, r * 0.55, color);
  dot(ctx, hx, hy, r * 0.26, "#ffffff");
}

export function drawSwitchSwapAnim(
  ctx: CanvasRenderingContext2D,
  a: SwapEnd,
  b: SwapEnd,
  headR: number,
  p: number,
  t: number,
  variant: number,
): void {
  const R = Math.max(4, headR);
  const e = easeInOut(clamp01(p));
  const dist = Math.hypot(b.x - a.x, b.y - a.y) || 1;
  const bend = Math.min(60, dist * 0.25);
  ctx.save();
  ctx.lineCap = "round";
  switch (variant) {
    case 1: { // Soul orbs: each worm's colour flies to the other along mirrored arcs.
      soul(ctx, a, b, bend, e, R, a.colorFrom);
      soul(ctx, b, a, bend, e, R, b.colorFrom);
      ring(ctx, a.x, a.y, R * (1 + e * 1.5), R * 0.18 * (1 - e), rgba(a.colorFrom, 1 - e));
      ring(ctx, b.x, b.y, R * (1 + e * 1.5), R * 0.18 * (1 - e), rgba(b.colorFrom, 1 - e));
      break;
    }
    case 2: { // Ghost trails: translucent mini-worms wriggle across.
      for (const [from, to] of [[a, b], [b, a]] as [SwapEnd, SwapEnd][]) {
        for (let k = 0; k < 7; k++) {
          const u = clamp01(e - k * 0.05);
          const [x, y] = bez(from.x, from.y, to.x, to.y, bend * (from === a ? 1 : -1), u);
          const wob = Math.sin(t * 10 + k) * R * 0.25;
          dot(ctx, x, y + wob, R * 0.42 * (1 - k * 0.1), rgba(from.colorFrom, 0.5 * (1 - k * 0.12)));
        }
      }
      break;
    }
    case 3: { // Lightning link: a jagged bolt connects the two, flashing while colours drain over.
      const segs = 9;
      ctx.strokeStyle = rgba("#ffffff", 0.35 + 0.6 * Math.abs(Math.sin(t * 24)));
      ctx.lineWidth = R * 0.16;
      ctx.beginPath();
      ctx.moveTo(a.x, a.y);
      for (let k = 1; k < segs; k++) {
        const u = k / segs;
        const [x, y] = bez(a.x, a.y, b.x, b.y, 0, u);
        const j = (rnd(k + Math.floor(t * 18)) - 0.5) * R * 1.6 * Math.sin(u * Math.PI);
        ctx.lineTo(x + j, y + j * 0.4);
      }
      ctx.lineTo(b.x, b.y);
      ctx.stroke();
      soul(ctx, a, b, 0, e, R * 0.8, a.colorFrom);
      soul(ctx, b, a, 0, e, R * 0.8, b.colorFrom);
      break;
    }
    case 4: { // Portals: a ring opens over each head; the orb dives in and pops out of the other.
      const open = clamp01(p / 0.25), close = clamp01((1 - p) / 0.25);
      const rr = R * 1.7 * Math.min(open, close);
      for (const end of [a, b]) {
        ctx.save();
        ctx.translate(end.x, end.y);
        ctx.rotate(t * 2.2);
        ctx.strokeStyle = rgba(end.colorTo, 0.9);
        ctx.lineWidth = R * 0.22;
        ctx.setLineDash([rr * 0.8, rr * 0.5]);
        ctx.beginPath(); ctx.arc(0, 0, Math.max(0.5, rr), 0, TAU); ctx.stroke();
        ctx.restore();
      }
      if (p > 0.25 && p < 0.75) { // mid-flight between portals (subtle, mostly hidden)
        const u = (p - 0.25) / 0.5;
        dot(ctx, ...bez(a.x, a.y, b.x, b.y, bend, u), R * 0.3, rgba(a.colorFrom, 0.35));
        dot(ctx, ...bez(b.x, b.y, a.x, a.y, bend, u), R * 0.3, rgba(b.colorFrom, 0.35));
      }
      break;
    }
    case 5: { // Twin beams: two straight beams sweep the colours across, head-on.
      for (const [from, to, off] of [[a, b, -1], [b, a, 1]] as [SwapEnd, SwapEnd, number][]) {
        const d = Math.hypot(to.x - from.x, to.y - from.y) || 1;
        const nx = -(to.y - from.y) / d * R * 0.5 * off, ny = (to.x - from.x) / d * R * 0.5 * off;
        const u0 = clamp01(e * 1.25 - 0.25), u1 = clamp01(e * 1.25);
        const [x0, y0] = bez(from.x + nx, from.y + ny, to.x + nx, to.y + ny, 0, u0);
        const [x1, y1] = bez(from.x + nx, from.y + ny, to.x + nx, to.y + ny, 0, u1);
        ctx.strokeStyle = rgba(from.colorFrom, 0.85);
        ctx.lineWidth = R * 0.35;
        ctx.beginPath(); ctx.moveTo(x0, y0); ctx.lineTo(x1, y1); ctx.stroke();
        dot(ctx, x1, y1, R * 0.3, "#ffffff");
      }
      break;
    }
    case 6: { // Vortex exchange: particles spiral OUT of each head, ACROSS, and IN to the other.
      for (const [from, to] of [[a, b], [b, a]] as [SwapEnd, SwapEnd][]) {
        for (let k = 0; k < 8; k++) {
          const u = clamp01(e * 1.15 - k * 0.02 - 0.05);
          const [x, y] = bez(from.x, from.y, to.x, to.y, bend * (from === a ? 1 : -1), u);
          const ang = t * 6 + k * (TAU / 8);
          const rr = R * (0.9 - 0.75 * Math.sin(u * Math.PI));
          dot(ctx, x + Math.cos(ang) * rr, y + Math.sin(ang) * rr, R * 0.16, rgba(from.colorFrom, 0.8));
        }
      }
      break;
    }
    case 7: { // Orbit half-turn: both heads' colours ride a half orbit around the midpoint.
      const mx = (a.x + b.x) / 2, my = (a.y + b.y) / 2;
      const baseAng = Math.atan2(a.y - my, a.x - mx);
      const rad = dist / 2;
      for (const [end, phase] of [[a, 0], [b, Math.PI]] as [SwapEnd, number][]) {
        const ang = baseAng + phase + e * Math.PI;
        const x = mx + Math.cos(ang) * rad, y = my + Math.sin(ang) * rad;
        for (let k = 1; k <= 4; k++) {
          const ta = ang - k * 0.09;
          dot(ctx, mx + Math.cos(ta) * rad, my + Math.sin(ta) * rad, R * (0.4 - k * 0.07), rgba(end.colorFrom, 0.5 - k * 0.1));
        }
        dot(ctx, x, y, R * 0.5, end.colorFrom);
        dot(ctx, x, y, R * 0.22, "#ffffff");
      }
      break;
    }
    case 8: { // Smoke swap: a puff swallows each head; they reappear (recoloured) from smoke.
      const inP = clamp01(p / 0.35), outP = clamp01((p - 0.65) / 0.35);
      for (const end of [a, b]) {
        for (let k = 0; k < 12; k++) {
          const ang = (k / 12) * TAU + rnd(k) * 2;
          const drift = R * (0.4 + rnd(k + 9) * 1.2) * (inP < 1 ? easeOut(inP) : 1 + outP);
          const alpha = inP < 1 ? 0.5 * inP : 0.5 * (1 - outP);
          dot(ctx, end.x + Math.cos(ang) * drift, end.y + Math.sin(ang) * drift, R * 0.34, rgba("#cbd5e1", alpha));
        }
      }
      break;
    }
    case 9: { // Spark stream: a dense stream of tiny sparks flows both ways.
      for (const [from, to] of [[a, b], [b, a]] as [SwapEnd, SwapEnd][]) {
        for (let k = 0; k < 10; k++) {
          const u = (e * 1.4 + k * 0.1) % 1;
          if (u > e + 0.15) continue;
          const [x, y] = bez(from.x, from.y, to.x, to.y, bend * (from === a ? 0.6 : -0.6), u);
          const jx = (rnd(k + 40) - 0.5) * R * 0.5, jy = (rnd(k + 80) - 0.5) * R * 0.5;
          dot(ctx, x + jx, y + jy, R * (0.1 + rnd(k) * 0.12), rgba(from.colorFrom, 0.9));
        }
      }
      break;
    }
    case 10: { // Glitch teleport: heads shatter into pixels that reassemble at the other end.
      for (const [from, to] of [[a, b], [b, a]] as [SwapEnd, SwapEnd][]) {
        for (let k = 0; k < 10; k++) {
          const u = clamp01(e + (rnd(k) - 0.5) * 0.12);
          const [x, y] = bez(from.x, from.y, to.x, to.y, (rnd(k + 3) - 0.5) * bend * 2, u);
          const sz = R * (0.24 + rnd(k + 6) * 0.2);
          ctx.fillStyle = rgba(from.colorFrom, 0.85);
          ctx.fillRect(x - sz / 2 + (rnd(k + 12) - 0.5) * R * 0.7, y - sz / 2 + (rnd(k + 17) - 0.5) * R * 0.7, sz, sz);
        }
      }
      break;
    }
    case 11: { // Yin-yang: both colours orbit the midpoint, tightening, then snap outward swapped.
      const mx = (a.x + b.x) / 2, my = (a.y + b.y) / 2;
      const baseAng = Math.atan2(a.y - my, a.x - mx);
      const rad = (dist / 2) * (1 - 0.6 * Math.sin(e * Math.PI));
      for (const [end, phase] of [[a, 0], [b, Math.PI]] as [SwapEnd, number][]) {
        const ang = baseAng + phase + e * TAU;
        dot(ctx, mx + Math.cos(ang) * rad, my + Math.sin(ang) * rad, R * 0.5, end.colorFrom);
        dot(ctx, mx + Math.cos(ang) * rad, my + Math.sin(ang) * rad, R * 0.2, "#ffffff");
      }
      ring(ctx, mx, my, R * 0.6 + Math.sin(e * Math.PI) * R, R * 0.12, rgba("#ffffff", 0.35 * Math.sin(e * Math.PI)));
      break;
    }
    case 12: { // Comet crossover: two comets (like the red missiles) pass each other mid-air.
      for (const [from, to, s] of [[a, b, 1], [b, a, -1]] as [SwapEnd, SwapEnd, number][]) {
        const u = e;
        ctx.strokeStyle = rgba(from.colorFrom, 0.95);
        ctx.lineWidth = R * 0.2;
        ctx.beginPath();
        const [x0, y0] = bez(from.x, from.y, to.x, to.y, bend * 1.4 * s, Math.max(0, u - 0.12));
        const [x1, y1] = bez(from.x, from.y, to.x, to.y, bend * 1.4 * s, u);
        ctx.moveTo(x0, y0); ctx.lineTo(x1, y1); ctx.stroke();
        dot(ctx, x1, y1, R * 0.24, "#ffffff");
      }
      break;
    }
    case 13: { // DNA helix: the two colours braid around the connecting line.
      for (let k = 0; k < 22; k++) {
        const u = k / 21;
        if (u > e * 1.15) break;
        const [x, y] = bez(a.x, a.y, b.x, b.y, 0, u);
        const ang = u * TAU * 2 + t * 4;
        const d = Math.hypot(b.x - a.x, b.y - a.y) || 1;
        const nx = -(b.y - a.y) / d, ny = (b.x - a.x) / d;
        const off = Math.sin(ang) * R * 0.8;
        dot(ctx, x + nx * off, y + ny * off, R * 0.18, rgba(a.colorFrom, 0.9));
        dot(ctx, x - nx * off, y - ny * off, R * 0.18, rgba(b.colorFrom, 0.9));
      }
      break;
    }
    case 14: { // Firework handoff: launch burst, rocket across, arrival burst.
      for (const [from, to, s] of [[a, b, 1], [b, a, -1]] as [SwapEnd, SwapEnd, number][]) {
        if (p < 0.15) { // launch flash
          const lp = p / 0.15;
          ring(ctx, from.x, from.y, R * (0.4 + lp * 1.6), R * 0.16 * (1 - lp), rgba(from.colorFrom, 1 - lp));
        } else if (p < 0.75) {
          const u = (p - 0.15) / 0.6;
          soul(ctx, from, to, bend * s, easeInOut(u), R * 0.9, from.colorFrom);
        } else { // arrival burst at the OTHER head, in the arriving colour
          const bp = (p - 0.75) / 0.25;
          for (let k = 0; k < 8; k++) {
            const ang = (k / 8) * TAU + rnd(k) * 0.6;
            const dd = R * (0.3 + easeOut(bp) * 1.8);
            dot(ctx, to.x + Math.cos(ang) * dd, to.y + Math.sin(ang) * dd, R * 0.15 * (1 - bp), rgba(from.colorFrom, 1 - bp));
          }
        }
      }
      break;
    }
    case 15: { // Pulse rings relay: expanding rings hop the gap like a signal.
      const hops = 4;
      for (const [from, to] of [[a, b], [b, a]] as [SwapEnd, SwapEnd][]) {
        for (let k = 0; k < hops; k++) {
          const u = clamp01(e * (hops + 1) - k) ;
          if (u <= 0 || u >= 1) continue;
          const [x, y] = bez(from.x, from.y, to.x, to.y, bend * (from === a ? 0.5 : -0.5), (k + u) / (hops + 1));
          ring(ctx, x, y, R * (0.2 + u * 1.2), R * 0.14 * (1 - u), rgba(from.colorFrom, 1 - u));
        }
      }
      break;
    }
    case 16: { // Shadow dash: a dark silhouette dashes over, colour blooming on arrival.
      for (const [from, to, s] of [[a, b, 1], [b, a, -1]] as [SwapEnd, SwapEnd, number][]) {
        const u = e;
        const [x, y] = bez(from.x, from.y, to.x, to.y, bend * 0.6 * s, u);
        for (let k = 1; k <= 5; k++) {
          const [tx, ty] = bez(from.x, from.y, to.x, to.y, bend * 0.6 * s, Math.max(0, u - k * 0.06));
          dot(ctx, tx, ty, R * 0.45, rgba("#0f172a", 0.5 - k * 0.08));
        }
        dot(ctx, x, y, R * 0.5, rgba("#0f172a", 0.9));
        dot(ctx, x, y, R * 0.5 * clamp01((e - 0.7) / 0.3), from.colorFrom); // colour blooms near arrival
      }
      break;
    }
    case 17: { // Star spin: spinning stars swap along shallow arcs.
      for (const [from, to, s] of [[a, b, 1], [b, a, -1]] as [SwapEnd, SwapEnd, number][]) {
        const [x, y] = bez(from.x, from.y, to.x, to.y, bend * 0.8 * s, e);
        ctx.save();
        ctx.translate(x, y);
        ctx.rotate(t * 8 * s);
        ctx.fillStyle = from.colorFrom;
        ctx.beginPath();
        for (let k = 0; k < 10; k++) {
          const ang = (k / 10) * TAU;
          const rr = k % 2 === 0 ? R * 0.7 : R * 0.28;
          ctx.lineTo(Math.cos(ang) * rr, Math.sin(ang) * rr);
        }
        ctx.closePath(); ctx.fill();
        dot(ctx, 0, 0, R * 0.2, "#ffffff");
        ctx.restore();
      }
      break;
    }
    case 18: { // Colour drip swap: colour "drains" from each head and refills at the other.
      for (const [from, to, s] of [[a, b, 1], [b, a, -1]] as [SwapEnd, SwapEnd, number][]) {
        // drain ring shrinking at the source...
        ring(ctx, from.x, from.y, R * (1.4 - e * 1.1), R * 0.2 * (1 - e), rgba(from.colorFrom, 0.8 * (1 - e)));
        // ...droplets along the way...
        for (let k = 0; k < 6; k++) {
          const u = clamp01(e * 1.2 - k * 0.07);
          if (u <= 0) continue;
          const [x, y] = bez(from.x, from.y, to.x, to.y, bend * 0.4 * s, u);
          dot(ctx, x, y + Math.sin(u * Math.PI) * -R * 0.3, R * (0.22 - k * 0.02), rgba(from.colorFrom, 0.85));
        }
        // ...filling ring growing at the destination.
        ring(ctx, to.x, to.y, R * (0.3 + e * 1.1), R * 0.2 * e, rgba(from.colorFrom, 0.8 * e));
      }
      break;
    }
    // ===== portal family: riffs on #4. Shared skeleton: a gate opens over each head
    // (0-0.25), the occupants are "inside" (0.25-0.75, heads hidden via HIDE_HEADS, a faint
    // transit hint crosses), the gate closes (0.75-1). Each variant is a different gate look,
    // tinted with the ARRIVING colour (colorTo) so the portal foreshadows the new owner.
    case 19: case 20: case 21: case 22: case 23: case 24: case 25: case 26: case 27: case 28:
    case 29: case 30: case 31: {
      const V = variant;
      const open = clamp01(p / 0.25), close = clamp01((1 - p) / 0.25);
      const win = Math.min(open, close);          // 0 -> 1 -> 0 gate envelope
      const rr = R * 1.8 * win;                    // gate radius
      const mid = p > 0.25 && p < 0.75;
      // Firework travel (the #14 look), reused by #30 (under the gates) and #31 (over them).
      const fireworkTravel = () => {
        for (const [from, to, s] of [[a, b, 1], [b, a, -1]] as [SwapEnd, SwapEnd, number][]) {
          if (p < 0.25) { // launch flash while the gate opens
            const lp = p / 0.25;
            ring(ctx, from.x, from.y, R * (0.4 + lp * 1.6), R * 0.16 * (1 - lp), rgba(from.colorFrom, 1 - lp));
          } else if (p < 0.75) { // rocket between the gates
            const u = (p - 0.25) / 0.5;
            soul(ctx, from, to, bend * s, easeInOut(u), R * 0.9, from.colorFrom);
          } else { // arrival burst in the arriving colour as the gate closes
            const bp = (p - 0.75) / 0.25;
            for (let k = 0; k < 8; k++) {
              const ang = (k / 8) * TAU + rnd(k) * 0.6;
              const dd = R * (0.3 + easeOut(bp) * 1.8);
              dot(ctx, to.x + Math.cos(ang) * dd, to.y + Math.sin(ang) * dd, R * 0.15 * (1 - bp), rgba(from.colorFrom, 1 - bp));
            }
          }
        }
      };
      if (V === 29 && mid) { // event horizon gates + FULL soul-orb travel (the #1 look)
        const u = easeInOut((p - 0.25) / 0.5);
        soul(ctx, a, b, bend, u, R, a.colorFrom);
        soul(ctx, b, a, bend, u, R, b.colorFrom);
      } else if (V === 30) { // fireworks UNDER the gates (rocket dips behind the discs)
        fireworkTravel();
      } else if (mid && V !== 25 && V !== 31) {
        // Faint transit hint between the gates (except the tunnel, which shows its own).
        const u = (p - 0.25) / 0.5;
        dot(ctx, ...bez(a.x, a.y, b.x, b.y, bend, u), R * 0.28, rgba(a.colorFrom, 0.3));
        dot(ctx, ...bez(b.x, b.y, a.x, a.y, bend, u), R * 0.28, rgba(b.colorFrom, 0.3));
      }
      for (const end of [a, b]) {
        const col = end.colorTo;
        ctx.save();
        ctx.translate(end.x, end.y);
        if (V === 19) { // vortex spiral: two spiral arms winding into the centre
          ctx.rotate(t * 3);
          ctx.strokeStyle = rgba(col, 0.9);
          ctx.lineWidth = R * 0.16;
          for (let arm = 0; arm < 2; arm++) {
            ctx.beginPath();
            for (let k = 0; k <= 20; k++) {
              const u = k / 20;
              const ang = arm * Math.PI + u * TAU * 1.4;
              const sr = rr * (1 - u * 0.9);
              const x = Math.cos(ang) * sr, y = Math.sin(ang) * sr;
              if (k === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
            }
            ctx.stroke();
          }
          dot(ctx, 0, 0, rr * 0.18, rgba("#ffffff", 0.8 * win));
        } else if (V === 20) { // iris gate: mechanical wedges opening from the centre
          ctx.rotate(t * 0.8);
          ctx.fillStyle = rgba(col, 0.85);
          const blades = 6, gap = win * rr;
          for (let k = 0; k < blades; k++) {
            ctx.save();
            ctx.rotate((k / blades) * TAU);
            ctx.beginPath();
            ctx.moveTo(gap * 0.35, 0);
            ctx.lineTo(rr * 1.15, -rr * 0.4);
            ctx.lineTo(rr * 1.15, rr * 0.4);
            ctx.closePath();
            ctx.fill();
            ctx.restore();
          }
          ring(ctx, 0, 0, rr * 1.15, R * 0.12, rgba(col, 0.9));
        } else if (V === 21 || V === 29 || V === 30 || V === 31) { // event horizon: black disc + hot accretion ring
          dot(ctx, 0, 0, rr, rgba("#02040a", 0.95));
          ring(ctx, 0, 0, rr, R * 0.2, rgba(col, 0.95));
          ctx.rotate(t * 5);
          for (let k = 0; k < 3; k++) { // orbiting hot spots on the rim
            const ang = (k / 3) * TAU;
            dot(ctx, Math.cos(ang) * rr, Math.sin(ang) * rr, R * 0.14, rgba("#ffffff", 0.9 * win));
          }
        } else if (V === 22) { // gyro rings: three tilted ellipses spinning up
          ctx.strokeStyle = rgba(col, 0.85);
          ctx.lineWidth = R * 0.12;
          for (let k = 0; k < 3; k++) {
            ctx.save();
            ctx.rotate(t * (1.5 + k * 0.7) + k * 1.1);
            ctx.beginPath();
            ctx.ellipse(0, 0, rr, rr * (0.25 + k * 0.12), 0, 0, TAU);
            ctx.stroke();
            ctx.restore();
          }
        } else if (V === 23) { // ripple pool: a flat pool with rings rippling outward
          ctx.strokeStyle = rgba(col, 0.8);
          for (let k = 0; k < 3; k++) {
            const u = ((t * 0.7 + k / 3) % 1);
            ctx.lineWidth = R * 0.14 * (1 - u);
            ctx.beginPath();
            ctx.ellipse(0, R * 0.25, rr * (0.3 + u), rr * (0.12 + u * 0.4), 0, 0, TAU);
            ctx.stroke();
          }
          ctx.fillStyle = rgba(col, 0.35 * win);
          ctx.beginPath(); ctx.ellipse(0, R * 0.25, rr * 0.5, rr * 0.2, 0, 0, TAU); ctx.fill();
        } else if (V === 24) { // rune circle: dashed magic ring + orbiting glyphs
          ctx.rotate(t * 1.2);
          ctx.strokeStyle = rgba(col, 0.9);
          ctx.lineWidth = R * 0.12;
          ctx.setLineDash([rr * 0.35, rr * 0.22]);
          ctx.beginPath(); ctx.arc(0, 0, Math.max(0.5, rr), 0, TAU); ctx.stroke();
          ctx.setLineDash([]);
          for (let k = 0; k < 5; k++) { // small glyph squares orbiting counter-wise
            const ang = -t * 2.4 + (k / 5) * TAU;
            const gx = Math.cos(ang) * rr * 0.72, gy = Math.sin(ang) * rr * 0.72;
            const sz = R * 0.22 * win;
            ctx.save(); ctx.translate(gx, gy); ctx.rotate(ang);
            ctx.fillStyle = rgba("#ffffff", 0.85 * win);
            ctx.fillRect(-sz / 2, -sz / 2, sz, sz);
            ctx.restore();
          }
        } else if (V === 26) { // shard gate: spinning triangular shards forming a ring
          ctx.rotate(t * 2.6);
          ctx.fillStyle = rgba(col, 0.85);
          for (let k = 0; k < 7; k++) {
            ctx.save();
            ctx.rotate((k / 7) * TAU);
            ctx.beginPath();
            ctx.moveTo(rr * 0.55, 0);
            ctx.lineTo(rr * 1.1, -R * 0.28);
            ctx.lineTo(rr * 1.1, R * 0.28);
            ctx.closePath(); ctx.fill();
            ctx.restore();
          }
        } else if (V === 27) { // storm gate: ring with little lightning arcs sparking off the rim
          ring(ctx, 0, 0, rr, R * 0.16, rgba(col, 0.9));
          ctx.strokeStyle = rgba("#ffffff", 0.8 * win);
          ctx.lineWidth = R * 0.1;
          for (let k = 0; k < 5; k++) {
            const seedI = k + Math.floor(t * 14);
            const ang = rnd(seedI) * TAU;
            ctx.beginPath();
            let sx = Math.cos(ang) * rr, sy = Math.sin(ang) * rr;
            ctx.moveTo(sx, sy);
            for (let j = 1; j <= 3; j++) {
              sx += Math.cos(ang) * R * 0.35 + (rnd(seedI + j * 7) - 0.5) * R * 0.6;
              sy += Math.sin(ang) * R * 0.35 + (rnd(seedI + j * 13) - 0.5) * R * 0.6;
              ctx.lineTo(sx, sy);
            }
            ctx.stroke();
          }
        } else if (V === 28) { // ink well: a soft wobbling blob that swallows the head
          ctx.fillStyle = rgba(col, 0.8);
          ctx.beginPath();
          for (let k = 0; k <= 20; k++) {
            const ang = (k / 20) * TAU;
            const wob = 1 + 0.16 * Math.sin(ang * 3 + t * 5) + 0.1 * Math.sin(ang * 5 - t * 3.2);
            const x = Math.cos(ang) * rr * wob, y = Math.sin(ang) * rr * wob;
            if (k === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
          }
          ctx.closePath(); ctx.fill();
          dot(ctx, 0, 0, rr * 0.3, rgba("#02040a", 0.8 * win));
        } else if (V === 25) { // wormhole tunnel: gates + a translucent tube with travel rings
          ring(ctx, 0, 0, rr, R * 0.18, rgba(col, 0.9));
          ring(ctx, 0, 0, rr * 0.6, R * 0.1, rgba(col, 0.5));
        }
        ctx.restore();
      }
      if (V === 25) { // the tunnel between the gates + rings sliding through it
        ctx.strokeStyle = rgba("#8ea6ff", 0.25 * win);
        ctx.lineWidth = R * 2.2;
        ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y); ctx.stroke();
        for (let k = 0; k < 4; k++) {
          const u = ((p * 1.6 + k / 4) % 1);
          const [x, y] = bez(a.x, a.y, b.x, b.y, 0, u);
          ring(ctx, x, y, R * (0.5 + Math.sin(u * Math.PI) * 0.5), R * 0.1, rgba("#ffffff", 0.4 * win));
        }
      }
      if (V === 31) fireworkTravel(); // fireworks OVER the gates (rocket sails across the discs)
      break;
    }
    default: { // fallback: soul orbs
      soul(ctx, a, b, bend, e, R, a.colorFrom);
      soul(ctx, b, a, bend, e, R, b.colorFrom);
      break;
    }
  }
  ctx.restore();
}

// ===== in-game switcharoonie scene (design #31 generalised to N worms, any owner mapping) =====
// The real switch is PLAYER-level: every worm a player owns moves together to one new owner, so
// combinations are 1->many, many->1, many->many. Worms never move - ownership (head colour) swaps -
// so the scene opens an event-horizon portal over every worm and flies a firework "soul" of each
// worm's NEW owner colour to it, launched from the CENTROID of that new owner's OLD worms (where the
// colour comes from). Fireworks draw OVER the portals (the #31 look). `p` = swap progress 0..1 over
// the freeze hold; heads flip colour at go (p=1), which the closing black discs cover.
export interface SwitchWorm { x: number; y: number; headR: number; playerId: any }
export function drawSwitchScene(
  ctx: CanvasRenderingContext2D,
  worms: SwitchWorm[],
  ownerMap: Record<string, any>,
  colorOf: (playerId: any) => string,
  p: number,
  t: number,
): void {
  if (!worms.length || !ownerMap) return;
  const win = Math.min(clamp01(p / 0.25), clamp01((1 - p) / 0.25)); // gate envelope 0->1->0
  const centroidOf = (pid: any): { x: number; y: number } | null => {
    let sx = 0, sy = 0, n = 0;
    for (const w of worms) if (w.playerId === pid) { sx += w.x; sy += w.y; n++; }
    return n ? { x: sx / n, y: sy / n } : null;
  };
  ctx.save();
  ctx.lineCap = "round";
  // 1) Event-horizon portals UNDER, tinted with each worm's ARRIVING colour.
  // Sizes are anchored to the head radius but with PIXEL floors: on a big map the
  // head is a few pixels, and FX that scale with it become unfollowable specks.
  for (const w of worms) {
    const R = Math.max(6, w.headR);
    const newOwner = ownerMap[w.playerId];
    const col = colorOf(newOwner !== undefined ? newOwner : w.playerId) || "#ffffff";
    const rr = R * 1.8 * win;
    dot(ctx, w.x, w.y, rr, "rgba(2,4,10,0.95)");
    ring(ctx, w.x, w.y, rr, R * 0.2, rgba(col, 0.95));
    for (let k = 0; k < 3; k++) {
      const ang = t * 5 + (k / 3) * TAU;
      dot(ctx, w.x + Math.cos(ang) * rr, w.y + Math.sin(ang) * rr, R * 0.14, rgba("#ffffff", 0.9 * win));
    }
  }
  // 2) Firework souls OVER: each worm's new colour flies in from that owner's old cluster.
  for (const w of worms) {
    const newOwner = ownerMap[w.playerId];
    if (newOwner === undefined) continue;
    const src = centroidOf(newOwner);
    if (!src) continue;
    const R = Math.max(6, w.headR);
    // The travelling soul is the thing the eye must FOLLOW across the whole arena,
    // so it gets a hard readable minimum in pixels, independent of map scale (a big
    // map shrinks heads, and a soul scaled off them was a near-invisible speck).
    const S = Math.max(10, R * 0.9);
    const col = colorOf(newOwner) || "#ffffff";
    const to = { x: w.x, y: w.y };
    const dist = Math.hypot(to.x - src.x, to.y - src.y) || 1;
    const bend = Math.min(60, dist * 0.25);
    if (p < 0.25) { // launch flash while the gate opens
      const lp = p / 0.25;
      ring(ctx, src.x, src.y, S * (0.45 + lp * 1.8), S * 0.18 * (1 - lp), rgba(col, 1 - lp));
    } else if (p < 0.75) { // rocket between the gates
      soul(ctx, src, to, bend, easeInOut((p - 0.25) / 0.5), S, col);
    } else { // arrival burst in the arriving colour as the gate closes
      const bp = (p - 0.75) / 0.25;
      for (let k = 0; k < 8; k++) {
        const ang = (k / 8) * TAU + rnd(k) * 0.6;
        const dd = S * (0.35 + easeOut(bp) * 1.8);
        dot(ctx, to.x + Math.cos(ang) * dd, to.y + Math.sin(ang) * dd, S * 0.16 * (1 - bp), rgba(col, 1 - bp));
      }
    }
  }
  ctx.restore();
}
